import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useAuth } from '../contexts/AuthContext';
import {
  createMfaEnrollment,
  saveAdminMfaRecord,
  verifyTotpCode,
  type MfaEnrollmentPayload,
} from '../services/MfaService';
import { brand } from '../theme';

const MfaSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user, mfaEnrolled, mfaVerified, refreshAuthState, completeMfaVerification } = useAuth();

  const [loadingSecret, setLoadingSecret] = useState(true);
  const [enrollment, setEnrollment] = useState<MfaEnrollmentPayload | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mfaEnrolled && mfaVerified) {
      navigate('/', { replace: true });
    }
  }, [mfaEnrolled, mfaVerified, navigate]);

  useEffect(() => {
    if (!user) return;

    const payload = createMfaEnrollment(user.email ?? 'admin');
    setEnrollment(payload);
    setLoadingSecret(false);
  }, [user]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user || !enrollment) return;

    setError(null);
    setSubmitting(true);

    try {
      const valid = verifyTotpCode(enrollment.secretBase32, verificationCode.trim());
      if (!valid) {
        setError('Doğrulama kodu hatalı. Authenticator\'daki güncel kodu girin.');
        return;
      }

      await saveAdminMfaRecord(user.uid, enrollment.secretBase32);
      completeMfaVerification();
      await refreshAuthState();
      navigate('/', { replace: true });
    } catch (err) {
      console.error(err);
      setError('2FA kaydedilemedi. Firestore kurallarını kontrol edin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: brand.slate900,
        px: 2,
        py: 4,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 480,
          border: `1px solid ${alpha('#FFFFFF', 0.08)}`,
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.35)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(brand.emerald, 0.15),
                color: brand.emerald,
                mb: 2,
              }}
            >
              <QrCode2Icon />
            </Box>
            <Typography variant="h5" fontWeight={700} textAlign="center">
              2FA Kurulumu
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
              Google Authenticator veya Authy ile QR kodu tarayın
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loadingSecret ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {enrollment?.otpauthUri && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    component="img"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(enrollment.otpauthUri)}`}
                    alt="MFA QR kodu"
                    sx={{ width: 200, height: 200 }}
                  />
                </Box>
              )}

              {enrollment?.secretBase32 && (
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 2 }}>
                  Manuel kod: <strong>{enrollment.secretBase32}</strong>
                </Typography>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Authenticator Kodu"
                  inputMode="numeric"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  sx={{ mb: 3 }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={submitting || verificationCode.length !== 6}
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                >
                  {submitting ? 'Kaydediliyor...' : '2FA\'yı Etkinleştir'}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MfaSetup;
